<template>
    <BModal
        :model-value="modal"
        scrollable
        centered
        title="Admin"
        @update:modelValue="updateModal"
        no-close-on-backdrop
        no-close-on-esc
    >
        <template #header>
            <div class="d-flex align-items-center justify-content-between modal-title w-100">
                <h5 class="">Admin</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <BForm @submit.stop.prevent>
            <BContainer>
                <BRow>
                    <BCol cols="12">
                        <BFormGroup label="Name *" label-for="name" class="text-start mb-3">
                            <BFormInput
                                id="name"
                                v-model="form.name"
                                placeholder="Name"
                                :class="{ 'is-invalid': v$.form.name.$dirty && v$.form.name.$invalid }"
                            />
                            <BFormInvalidFeedback :state="v$.form.name.$dirty && v$.form.name.$invalid">
                                {{ v$.form.name.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                </BRow>
                <BRow>
                    <BCol>
                        <BFormGroup label="Email *" label-for="email" class="text-start mb-3">
                            <BFormInput
                                id="email"
                                v-model="form.email"
                                placeholder="Email"
                                :class="{ 'is-invalid': v$.form.email.$dirty && v$.form.email.$invalid }"
                            />
                            <BFormInvalidFeedback :state="v$.form.email.$dirty && v$.form.email.$invalid">
                                {{ v$.form.email.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                </BRow>
                <BRow>
                    <BCol>
                        <BFormGroup label="Password" label-for="password" class="text-start mb-3">
                            <BFormInput
                                id="password"
                                type="password"
                                v-model="form.password"
                                placeholder="Password"
                                :class="{ 'is-invalid': v$.form.password.$dirty && v$.form.password.$invalid }"
                            />
                            <small v-if="props.selectedData?.uuid" class="text-muted">Leave blank to keep current password</small>
                            <BFormInvalidFeedback :state="v$.form.password.$dirty && v$.form.password.$invalid">
                                {{ v$.form.password.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                </BRow>
                <BRow>
                    <BCol>
                        <BFormGroup label="Confirm Password" label-for="password_confirmation" class="text-start mb-3">
                            <BFormInput
                                id="password_confirmation"
                                type="password"
                                v-model="form.password_confirmation"
                                placeholder="Confirm Password"
                                :class="{ 'is-invalid': v$.form.password_confirmation.$dirty && v$.form.password_confirmation.$invalid }"
                            />
                            <BFormInvalidFeedback :state="v$.form.password_confirmation.$dirty && v$.form.password_confirmation.$invalid">
                                {{ v$.form.password_confirmation.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                </BRow>
                <BRow>
                    <BCol>
                        <BFormGroup label="Position *" label-for="role" class="text-start mb-3">
                            <BFormSelect
                                id="role"
                                v-model="form.role"
                                :options="roleOptions"
                                class="form-control"
                                :class="{ 'is-invalid': v$.form.role.$dirty && v$.form.role.$invalid }"
                            >
                                <template #first>
                                    <option disabled value="">Select Position</option>
                                </template>
                            </BFormSelect>
                            <BFormInvalidFeedback :state="v$.form.role.$dirty && v$.form.role.$invalid">
                                {{ v$.form.role.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                </BRow>

                <BRow>
                    <BCol>
                        <BFormGroup label="Phone Number" label-for="phone" class="text-start mb-3">
                            <BFormInput
                                id="phone"
                                v-model="form.phone"
                                placeholder="Phone Number"
                                :class="{ 'is-invalid': v$.form.phone.$dirty && v$.form.phone.$invalid }"
                            />
                            <BFormInvalidFeedback :state="v$.form.phone.$dirty && v$.form.phone.$invalid">
                                {{ v$.form.phone.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                </BRow>

                <BRow>
                    <BCol>
                        <BFormGroup label="Address" label-for="address" class="text-start mb-3">
                            <BFormTextarea
                                id="address"
                                v-model="form.address"
                                placeholder="Address"
                                :class="{ 'is-invalid': v$.form.address.$dirty && v$.form.address.$invalid }"
                            />
                            <BFormInvalidFeedback :state="v$.form.address.$dirty && v$.form.address.$invalid">
                                {{ v$.form.address.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>
                </BRow>
            </BContainer>
        </BForm>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="primary" class="mr-2" @click="handleAction">Save</BButton>
                <BButton variant="danger" @click="updateModal(false)">Cancel</BButton>
            </div>
        </template>
    </BModal>
</template>

<script setup>
import axios from "axios";
import { defineProps, defineEmits, reactive, watch, computed } from "vue";
import { BModal, BRow, BCol, BForm, BFormInput, BButton, BFormSelect, BFormGroup, BFormInvalidFeedback, BContainer, BFormTextarea } from 'bootstrap-vue-next';
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, email, minLength, sameAs } from "@vuelidate/validators";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";

const auth = useAuthStore();
const $loading = useLoading();
const toast = useToast();

const hasRole = (allowedRoles) => {
    const userRoles = auth._roles;
    if (!userRoles) return false;
    const roles = Array.isArray(userRoles) ? userRoles : [userRoles];
    return roles.some(role => allowedRoles.includes(role));
};

const roleOptions = computed(() => {
    const options = [
        { value: 'Super Admin', text: 'Super Admin' },
        { value: 'Admin Office', text: 'Admin Office' },
        { value: 'Manager Driver', text: 'Manager Driver' },
    ];
    
    if (hasRole(['Admin Office']) && !hasRole(['Super Admin'])) {
         return options.filter(opt => opt.value !== 'Super Admin');
    }
    
    return options;
});

const props = defineProps({
    modal: Boolean,
    selectedData: Object,
});

const emit = defineEmits(['update:modal', 'addOrUpdate']);

const updateModal = (value) => {
    emit("update:modal", value);
}

const formData = {
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    role: "",
    phone: "",
    address: "",
    status: "Active",
};

const form = reactive({ ...formData });

watch(
    () => props.selectedData,
    (newData) => {
        if (newData && Object.keys(newData).length > 0) {
            Object.assign(form, { 
                ...newData, 
                password: "", 
                password_confirmation: "",
                role: newData.roles && newData.roles.length > 0 ? newData.roles[0].name : "" 
            });
        } else {
            Object.assign(form, { ...formData });
        }
    },
    { immediate: true }
);

const rules = computed(() => {
    const localRules = {
        form: {
            name: { required: helpers.withMessage("Name is required", required) },
            email: { required: helpers.withMessage("Email is required", required), email: helpers.withMessage("Invalid email format", email) },
            role: { required: helpers.withMessage("Position is required", required) },
            password: {},
            password_confirmation: {
                sameAsPassword: helpers.withMessage("Passwords must match", sameAs(form.password)),
            },
            phone: {},
            address: {},
            status: {},
        },
    };

    if (!props.selectedData?.uuid) {
        localRules.form.password = {
            required: helpers.withMessage("Password is required", required),
            minLength: helpers.withMessage("Password must be at least 6 characters", minLength(6)),
        };
    } else {
         if (form.password) {
            localRules.form.password = {
                minLength: helpers.withMessage("Password must be at least 6 characters", minLength(6)),
            };
        }
    }

    return localRules;
});

const v$ = useVuelidate(rules, { form });

const handleAction = () => {
    props.selectedData?.uuid ? update() : create();
};

const create = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({ color: "#489464", loader: "spinner" });
        try {
            const response = await axios.post(
                `${process.env.VUE_APP_API_URL}/admin/add`,
                form,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;
            if (res.status) {
                emit("addOrUpdate");
                toast.success(res.message);
                updateModal(false);
                Object.assign(form, { ...formData });
                v$.value.$reset();
            }
        } catch (error) {
             toast.error(error.response?.data?.message || "Failed to create user");
        } finally {
            loader.hide();
        }
    }
};

const update = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({ color: "#489464", loader: "spinner" });
        try {
            const payload = { ...form };
            if (!payload.password) {
                delete payload.password;
                delete payload.password_confirmation;
            }

            const response = await axios.put(
                `${process.env.VUE_APP_API_URL}/admin/edit/${props.selectedData.uuid}`,
                payload,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;
            if (res.status) {
                emit("addOrUpdate");
                toast.success(res.message);
                updateModal(false);
                Object.assign(form, { ...formData });
                v$.value.$reset();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update user");
        } finally {
            loader.hide();
        }
    }
};
</script>
